import { cloneDeep } from 'lodash';

const findSeriesToSort = ({ compareByGroups, seriesList, sortByValue }) => {
  if (!Array.isArray(seriesList) || seriesList.length === 0) return undefined;
  if (!sortByValue || sortByValue === 'default') return seriesList[0];

  const sortToken = String(sortByValue);
  const candidates = [sortToken];

  if (Array.isArray(compareByGroups)) {
    const matchingGroup = compareByGroups.find(
      (group) => group?.id != null && String(group.id) === sortToken
    );
    if (matchingGroup?.name != null) candidates.push(String(matchingGroup.name));
  }

  for (const token of candidates) {
    const match = seriesList.find(
      (series) =>
        (series?.id != null && String(series.id) === token) ||
        (series?.name != null && String(series.name) === token)
    );
    if (match) return match;
  }

  return seriesList[0];
};

const asArray = (value) => (Array.isArray(value) ? value : []);
const getNumericValue = (value) => (typeof value === 'number' ? value : 0);
const sumDataPoints = (currentValue, nextValue) =>
  getNumericValue(currentValue) + getNumericValue(nextValue);

const aggregateSeries = (selectedSeriesGroups) => {
  const seriesByKey = new Map();

  selectedSeriesGroups.forEach((seriesList) => {
    asArray(seriesList).forEach((series, index) => {
      const key = series?.id ?? series?.name ?? index;
      const existingSeries = seriesByKey.get(key);

      if (!existingSeries) {
        seriesByKey.set(key, cloneDeep(series));
        return;
      }

      const existingData = asArray(existingSeries.data);
      const nextData = asArray(series.data);

      existingSeries.data = existingData.map((value, dataIndex) =>
        sumDataPoints(value, nextData[dataIndex])
      );
    });
  });

  return Array.from(seriesByKey.values());
};

const filterChartCategories = ({ activeChart, selectedAnswerOptions }) => {
  const originalXAxisConfig = activeChart.xAxis || {};
  const allCategories = asArray(originalXAxisConfig.categories);
  const activeIndices = allCategories.reduce((acc, cat, i) => {
    if (selectedAnswerOptions.includes(cat)) acc.push(i);
    return acc;
  }, []);

  return {
    ...activeChart,
    series: cloneDeep(activeChart.series ?? []).map((series) => ({
      ...series,
      data: Array.isArray(series.data)
        ? series.data.filter((_, i) => activeIndices.includes(i))
        : series.data,
    })),
    xAxis: {
      ...originalXAxisConfig,
      categories: allCategories.filter((_, i) => activeIndices.includes(i)),
    },
  };
};

/**
 * Normalizes chart data after Answer Options dropdown selection.
 * @param {Object} params
 * @param {Object} params.activeChart Chart response selected for the current display mode.
 * @param {Array<string>} params.selectedAnswerOptions Selected answer option labels.
 * @returns {Object|undefined} Chart with a top-level series array when data is available.
 */
export const normalizeAnswerOptionChart = ({ activeChart, selectedAnswerOptions = [] }) => {
  if (!activeChart) return activeChart;

  // Standard chart shape: selected answers map to x-axis categories.
  if (Array.isArray(activeChart.series)) {
    return selectedAnswerOptions.length
      ? filterChartCategories({ activeChart, selectedAnswerOptions })
      : activeChart;
  }

  // Answer-option-keyed charts need a selection before a series can be rendered.
  if (!selectedAnswerOptions.length) return { ...activeChart, series: [] };

  const selectedSeriesGroups = selectedAnswerOptions
    .map((answerOption) => activeChart[answerOption]?.series)
    .filter((series) => Array.isArray(series));

  return {
    ...activeChart,
    series: aggregateSeries(selectedSeriesGroups),
  };
};

/**
 * Builds sorted bar chart for Highcharts
 *
 * @param {Object} params
 * @param {Object} params.activeChart
 * @param {Array}  params.compareByGroups Array of quota group objects
 * @param {string} params.sortByValue   "default"/selected quota group id
 * @param {string} params.sortOrder     "default"/"asc"/"desc"
 * @returns {Object} Chart configuration
 */
export const buildSortedBarChart = ({ activeChart, compareByGroups, sortByValue, sortOrder }) => {
  if (!activeChart) {
    return {
      chartPlotOptions: {
        series: {
          dataLabels: {
            allowOverlap: true,
          },
        },
      },
      chartSeries: [],
      chartXAxis: {},
    };
  }

  const originalXAxisConfig = activeChart.xAxis || {};
  const allCategories = asArray(originalXAxisConfig.categories);
  const originalCategories = allCategories;
  const originalSeries = cloneDeep(activeChart.series ?? []);
  const isSorted = sortOrder && sortOrder !== 'default';
  const isDescending = sortOrder === 'desc';

  if (!originalCategories.length || !originalSeries.length) {
    return {
      chartPlotOptions: {
        series: {
          dataLabels: { allowOverlap: true },
        },
      },
      chartSeries: originalSeries,
      chartXAxis: {
        ...originalXAxisConfig,
        reversed: false,
      },
    };
  }

  const buildPoint = ({ categoryName, dataValue }) => {
    if (typeof dataValue === 'number') {
      return { name: categoryName, y: dataValue };
    }

    if (Array.isArray(dataValue)) {
      const tupleY = dataValue
        .slice()
        .reverse()
        .find((value) => typeof value === 'number');
      return { name: categoryName, y: typeof tupleY === 'number' ? tupleY : null };
    }

    if (dataValue && typeof dataValue === 'object') {
      const { x, ...rest } = dataValue;
      return { ...rest, name: categoryName };
    }

    return { name: categoryName, y: null };
  };

  originalSeries.forEach((series) => {
    if (!Array.isArray(series.data)) return;

    series.data = series.data.map((value, index) =>
      buildPoint({
        categoryName: originalCategories[index],
        dataValue: value,
      })
    );
  });

  const seriesToSort = findSeriesToSort({
    compareByGroups,
    seriesList: originalSeries,
    sortByValue,
  });
  const referenceSeriesData = Array.isArray(seriesToSort?.data) ? seriesToSort.data : [];

  const categoryValuePairs = originalCategories.map((categoryName, index) => {
    const normalizedPoint = referenceSeriesData[index] ?? { name: categoryName, y: null };

    return {
      categoryName,
      sortValue: typeof normalizedPoint?.y === 'number' ? normalizedPoint.y : -Infinity,
    };
  });

  const sortedCategoryOrder = isSorted
    ? categoryValuePairs
        .slice()
        .sort((a, b) => {
          const difference = a.sortValue - b.sortValue;
          return isDescending ? -difference : difference;
        })
        .map(({ categoryName }) => categoryName)
    : originalCategories;

  const categoryIndexMap = new Map(
    originalCategories.map((categoryName, index) => [categoryName, index])
  );

  const remappedSeriesList = originalSeries.map((series) => {
    const seriesData = Array.isArray(series.data) ? series.data : [];

    const remappedSeriesData = sortedCategoryOrder.map((categoryName) => {
      const originalIndex = categoryIndexMap.get(categoryName);
      const originalPoint = originalIndex == null ? undefined : seriesData[originalIndex];

      return buildPoint({
        categoryName,
        dataValue: originalPoint,
      });
    });

    return {
      ...series,
      data: remappedSeriesData,
    };
  });

  return {
    chartPlotOptions: {
      series: {
        dataLabels: { allowOverlap: true },
        dataSorting: { enabled: false },
      },
    },
    chartSeries: remappedSeriesList,
    chartXAxis: {
      ...originalXAxisConfig,
      categories: sortedCategoryOrder,
      type: 'category',
    },
  };
};
